// Session storage — access + refresh tokens, expiry, and the user/org
// snapshot the screens read off the auth store.
//
// Storage key prefix is "bp_dashboard_" per ADR-0021/D3 — independent
// from apps/web-svelte's "bp_svelte_session_v1" key so a gerente logged
// in on both surfaces in two tabs has two independent token pairs.
//
// Token shapes mirror auth-svc's TokenPairResponse exactly so refresh
// rotation is a flat passthrough — no field renames at this layer.

import type { Role } from "@bitacorapro/api-types";

const STORAGE_KEY = "bp_dashboard_session_v1";

export type SessionUser = {
  id: string;
  email: string;
  displayName: string;
  rut: string | null;
  platformAdmin: boolean;
};

export type SessionOrg = {
  id: string;
  name: string;
  role: Role;
};

export type Session = {
  accessToken: string;
  accessTokenExpiresAt: string;
  refreshToken: string;
  refreshTokenExpiresAt: string;
  user: SessionUser;
  organization: SessionOrg;
};

const inBrowser = typeof window !== "undefined";

export function loadSession(): Session | null {
  if (!inBrowser) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    if (
      typeof parsed?.accessToken !== "string" ||
      typeof parsed?.refreshToken !== "string" ||
      !parsed?.user?.id
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveSession(session: Session): void {
  if (!inBrowser) return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  if (!inBrowser) return;
  window.localStorage.removeItem(STORAGE_KEY);
}

// True when the access token's expiry is within `skewMs` of now. The
// client uses this to refresh proactively on the way out instead of
// waiting for a 401 round-trip.
export function isAccessExpiring(session: Session, skewMs = 30_000): boolean {
  const exp = Date.parse(session.accessTokenExpiresAt);
  if (!Number.isFinite(exp)) return true;
  return exp - skewMs <= Date.now();
}
